import { deriveArrays } from "./derive";
import { normalizeAccess, type AccessResource } from "./types";

export type AccessKind =
  | "board"
  | "project"
  | "collection"
  | "schedule"
  | "concept";

interface AccessKindFields {
  collection: string;
  ownerField: string;
  parentField: string | null;
  // Boards and concepts are openable by link (get: if true in the rules).
  alwaysPublic: boolean;
  // A schedule has no list of its own: its project decides.
  ownList: boolean;
}

export const ACCESS_KINDS: Record<AccessKind, AccessKindFields> = {
  board: {
    collection: "projects",
    ownerField: "user",
    parentField: "associatedProjectId",
    alwaysPublic: true,
    ownList: true,
  },
  project: {
    collection: "projectsScheme",
    ownerField: "owner",
    parentField: null,
    alwaysPublic: false,
    ownList: true,
  },
  collection: {
    collection: "usersMyCollection",
    ownerField: "uid",
    parentField: "associatedProjectId",
    alwaysPublic: false,
    ownList: true,
  },
  schedule: {
    collection: "ffeSchedules",
    ownerField: "uid",
    parentField: "projectId",
    alwaysPublic: false,
    ownList: false,
  },
  concept: {
    collection: "concepts",
    ownerField: "owner_id",
    parentField: "current.project_id",
    alwaysPublic: true,
    ownList: true,
  },
};

type Doc = Record<string, unknown>;

const nonEmpty = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);

const read = (doc: object, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (v, k) => (v && typeof v === "object" ? (v as Doc)[k] : undefined),
      doc,
    );

// "" and absent both mean "not in a project".
export const parentProjectId = (kind: AccessKind, doc: object) => {
  const { parentField } = ACCESS_KINDS[kind];
  return parentField ? nonEmpty(read(doc, parentField)) : null;
};

// The item's project, by id: used only when it is the project the item's
// parent pointer names, so a caller can't hand it the wrong one.
export interface ProjectRef {
  id: string;
  doc: object;
}

export function toAccessResource(
  kind: AccessKind,
  doc: object,
  project?: ProjectRef | null,
): AccessResource {
  const fields = ACCESS_KINDS[kind];
  const d = doc as Doc;
  const owner = nonEmpty(d[fields.ownerField]) ?? "";
  return {
    owner,
    created_by: nonEmpty(d.created_by) ?? owner,
    team: nonEmpty(d.design_firm_id),
    access: fields.ownList ? normalizeAccess(d.access) : undefined,
    publicLink: fields.alwaysPublic || d.is_public === true,
    project:
      project && project.id === parentProjectId(kind, doc)
        ? toAccessResource("project", project.doc)
        : null,
  };
}

// The write that hands an item to a new owner (a leaver's shared items pass to
// the removing admin). It stamps created_by first, so a doc that never had one
// keeps its creator, and rebuilds readers/editors for the new owner.
export function ownerTransferFields(
  kind: AccessKind,
  doc: object,
  newOwner: string,
  project?: ProjectRef | null,
) {
  const resource = toAccessResource(kind, doc, project);
  return {
    [ACCESS_KINDS[kind].ownerField]: newOwner,
    created_by: resource.created_by,
    ...deriveArrays({ ...resource, owner: newOwner }),
  };
}
